import moment from 'moment'
import type { Moment } from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'

const DATE_CONFIRMATION_STORAGE_PREFIX = 'nl-date-confirmation'
const DATE_ACK_STORAGE_PREFIX = 'nl-date-acknowledgement'

export type SearchDateSelection =
  | {
    type: 'specific'
    checkIn: string
    checkOut: string
  }
  | {
    type: 'flexible'
    flexibleMonths: string
    flexibleNights: FLEXIBLE_DURATION_RANGE | string
  }
  | {
    type: 'anytime'
  }
  | {
    type: 'empty'
  }

interface FormDateSelectionParams {
  checkinDate?: Moment
  checkoutDate?: Moment
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  flexibleMonths?: string
  isAnytimeDateSelected?: boolean
}

interface ExtractedSearchParamsDateFields {
  checkIn?: string
  checkOut?: string
  flexibleNights?: string
  flexibleMonths?: string
}

function getStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  return window.sessionStorage
}

/** sessionStorage can throw (private mode / quota); never let that abort Search. */
function safeStorageSetItem(key: string, value: string): void {
  try {
    getStorage()?.setItem(key, value)
  } catch {
    // ignore
  }
}

function safeStorageRemoveItem(key: string): void {
  try {
    getStorage()?.removeItem(key)
  } catch {
    // ignore
  }
}

function safeStorageGetItem(key: string): string | undefined {
  try {
    return getStorage()?.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

function dateConfirmationKey(typeaheadId: string): string {
  return `${DATE_CONFIRMATION_STORAGE_PREFIX}:${typeaheadId}`
}

function dateAcknowledgementKey(typeaheadId: string): string {
  return `${DATE_ACK_STORAGE_PREFIX}:${typeaheadId}`
}

function normaliseMonthList(flexibleMonths: string): string {
  return flexibleMonths.split(',').filter(Boolean).join(',')
}

function serialiseDateSelection(selection: SearchDateSelection | undefined): string {
  if (!selection) return 'empty'

  if (selection.type === 'specific') {
    return `specific:${selection.checkIn}:${selection.checkOut}`
  }

  if (selection.type === 'flexible') {
    return `flexible:${normaliseMonthList(selection.flexibleMonths)}:${selection.flexibleNights}`
  }

  return selection.type
}

export function getSearchDateSelectionFromForm({
  checkinDate,
  checkoutDate,
  flexibleNights,
  flexibleMonths,
  isAnytimeDateSelected,
}: FormDateSelectionParams): SearchDateSelection {
  if (checkinDate && checkoutDate) {
    return {
      type: 'specific',
      checkIn: checkinDate.format(ISO_DATE_FORMAT),
      checkOut: checkoutDate.format(ISO_DATE_FORMAT),
    }
  }

  if (flexibleNights && flexibleMonths) {
    return {
      type: 'flexible',
      flexibleMonths,
      flexibleNights,
    }
  }

  if (isAnytimeDateSelected) {
    return { type: 'anytime' }
  }

  return { type: 'empty' }
}

export function getSearchDateSelectionFromExtractedDates(
  extractedDates: App.ExtractDatesResult | undefined,
): SearchDateSelection | undefined {
  if (!extractedDates) return undefined

  if (extractedDates.checkinDate && extractedDates.checkoutDate) {
    return getSearchDateSelectionFromExtractedSearchParams({
      checkIn: moment(extractedDates.checkinDate).format(ISO_DATE_FORMAT),
      checkOut: moment(extractedDates.checkoutDate).format(ISO_DATE_FORMAT),
    })
  }

  return getSearchDateSelectionFromExtractedSearchParams({
    flexibleMonths: extractedDates.flexibleMonths,
    flexibleNights: extractedDates.flexibleNights,
  })
}

export function getSearchDateSelectionFromExtractedSearchParams(
  extractedData: ExtractedSearchParamsDateFields | undefined,
): SearchDateSelection | undefined {
  if (!extractedData) return undefined

  if (extractedData.checkIn && extractedData.checkOut) {
    return {
      type: 'specific',
      checkIn: extractedData.checkIn,
      checkOut: extractedData.checkOut,
    }
  }

  if (extractedData.flexibleMonths && extractedData.flexibleNights) {
    return {
      type: 'flexible',
      flexibleMonths: extractedData.flexibleMonths,
      flexibleNights: extractedData.flexibleNights,
    }
  }

  return undefined
}

export function areSearchDateSelectionsEqual(
  first: SearchDateSelection | undefined,
  second: SearchDateSelection | undefined,
): boolean {
  return serialiseDateSelection(first) === serialiseDateSelection(second)
}

export function isSearchDateSelectionExtractedOverride(
  selection: SearchDateSelection | undefined,
): selection is Extract<SearchDateSelection, { type: 'specific' | 'flexible' }> {
  return selection?.type === 'specific' || selection?.type === 'flexible'
}

export interface ExtractCommittedPhraseInput {
  searchPhrase: string
  extractSourceInput: string | undefined
  committedSelectionPhrase: string | undefined
  selectedItemMainText: string | undefined
  currentDateSelection: SearchDateSelection
  extractedDateSelection?: SearchDateSelection
}

export interface ExtractCommittedPhraseResult {
  nextCommittedSelectionPhrase: string | undefined
  shouldSkipExtract: boolean
}

/**
 * Decides whether an incoming extract should be ignored because the phrase is
 * empty/stale, matches an already-committed recent-search/URL selection, or is
 * in the clear-selection race where searchItem cleared before the phrase.
 */
export function resolveExtractCommittedPhraseState({
  searchPhrase,
  extractSourceInput,
  committedSelectionPhrase,
  selectedItemMainText,
  currentDateSelection,
  extractedDateSelection,
}: ExtractCommittedPhraseInput): ExtractCommittedPhraseResult {
  let nextCommittedSelectionPhrase = committedSelectionPhrase

  // Phrase changed away from a previously committed selection — allow future extract again.
  if (nextCommittedSelectionPhrase && searchPhrase !== nextCommittedSelectionPhrase) {
    nextCommittedSelectionPhrase = undefined
  }

  // Clearing the Where field (or mid-edit) must not re-apply a previous extract.
  if (!searchPhrase.trim() || extractSourceInput !== searchPhrase) {
    return {
      nextCommittedSelectionPhrase,
      shouldSkipExtract: true,
    }
  }

  // Recent search / URL hydration: phrase matches the selected item and dates are already
  // committed. A late re-extract of the same phrase must not overwrite dates or AI-glow.
  // Only skip when form dates already match the extract — intermediate dates (e.g. 10–11)
  // left on the form after selecting a full “10–23” NL item must still accept the final extract.
  const hasNonAnytimeDates = currentDateSelection.type !== 'anytime'
  const extractAlreadyApplied = !!extractedDateSelection
    && areSearchDateSelectionsEqual(currentDateSelection, extractedDateSelection)

  if (
    selectedItemMainText
    && selectedItemMainText === searchPhrase
    && hasNonAnytimeDates
    && extractAlreadyApplied
  ) {
    return {
      nextCommittedSelectionPhrase: searchPhrase,
      shouldSkipExtract: true,
    }
  }

  // Clear-selection race: searchItem is gone but phrase still matches the committed recent search.
  if (
    nextCommittedSelectionPhrase
    && searchPhrase === nextCommittedSelectionPhrase
    && hasNonAnytimeDates
    && extractAlreadyApplied
  ) {
    return {
      nextCommittedSelectionPhrase,
      shouldSkipExtract: true,
    }
  }

  return {
    nextCommittedSelectionPhrase,
    shouldSkipExtract: false,
  }
}

export interface DateConfirmationMutableState {
  dateConfirmationPhrase?: string
  dateConfirmationBaseline?: SearchDateSelection
  dateConfirmationAppliedSelection?: SearchDateSelection
  /** Extracted dates the user has already been shown the soft-confirm banner for. */
  dateConfirmationAcknowledgedSelection?: SearchDateSelection
  dateConfirmationRequired?: boolean
  dateConfirmationViewed?: boolean
}

/**
 * Marks the current applied extract as acknowledged so the soft-confirm banner
 * is not shown again for the same dates.
 */
export function acknowledgeDateConfirmation(
  refData: DateConfirmationMutableState | null | undefined,
): void {
  if (!refData) return
  refData.dateConfirmationViewed = true
  refData.dateConfirmationAcknowledgedSelection = refData.dateConfirmationAppliedSelection
}

/**
 * Updates soft-confirm flags for an extract that passed the committed-phrase guards.
 * Returns whether the caller should notify that confirmation is required.
 */
export function updateDateConfirmationStateOnExtract(
  refData: DateConfirmationMutableState | null | undefined,
  {
    searchPhrase,
    currentDateSelection,
    extractedDateSelection,
  }: {
    searchPhrase: string
    currentDateSelection: SearchDateSelection
    extractedDateSelection: SearchDateSelection
  },
): boolean {
  if (!refData) return false

  // Same extracted dates already shown once — never re-prompt for them.
  if (areSearchDateSelectionsEqual(refData.dateConfirmationAcknowledgedSelection, extractedDateSelection)) {
    refData.dateConfirmationAppliedSelection = extractedDateSelection
    refData.dateConfirmationRequired = false
    refData.dateConfirmationViewed = true
    return false
  }

  const baselineSelection = refData.dateConfirmationBaseline ?? currentDateSelection

  if (!refData.dateConfirmationBaseline) {
    refData.dateConfirmationBaseline = currentDateSelection
    refData.dateConfirmationPhrase = searchPhrase
  }

  const previousAppliedSelection = refData.dateConfirmationAppliedSelection
  refData.dateConfirmationAppliedSelection = extractedDateSelection
  const needsConfirmation = !areSearchDateSelectionsEqual(baselineSelection, extractedDateSelection)
  refData.dateConfirmationRequired = needsConfirmation

  // Each new extracted date change needs its own confirmation pass. If the
  // applied selection changed, clear any prior "viewed" flag so Search blocks again.
  if (!needsConfirmation) {
    refData.dateConfirmationViewed = false
    return false
  }

  if (!areSearchDateSelectionsEqual(previousAppliedSelection, extractedDateSelection)) {
    refData.dateConfirmationViewed = false
  }

  return !refData.dateConfirmationViewed
}

/**
 * After confirmation flags are updated, decide whether to force the extract onto the form.
 */
export function shouldSkipForcedExtractApply({
  userHasEditedDates,
  lastForcedExtractKey,
  extractKey,
  currentDateSelection,
  extractedDateSelection,
}: {
  userHasEditedDates: boolean
  lastForcedExtractKey: string | undefined
  extractKey: string
  currentDateSelection: SearchDateSelection
  extractedDateSelection: SearchDateSelection
}): boolean {
  // Once the user opens/edits the date picker, stop forcing extracted dates back
  // over their changes (e.g. while reviewing the confirmation banner).
  if (userHasEditedDates) return true

  // We already applied this extract; form differs because the user edited dates.
  return lastForcedExtractKey === extractKey
    && !areSearchDateSelectionsEqual(currentDateSelection, extractedDateSelection)
}

function storeTypeaheadDateConfirmationBaseline(
  typeaheadId: string | undefined,
  baseline: SearchDateSelection | undefined,
) {
  if (!typeaheadId || !baseline) return
  safeStorageSetItem(dateConfirmationKey(typeaheadId), JSON.stringify(baseline))
}

export function getTypeaheadDateConfirmationBaseline(
  typeaheadId: string | undefined,
): SearchDateSelection | undefined {
  if (!typeaheadId) return undefined

  const value = safeStorageGetItem(dateConfirmationKey(typeaheadId))
  if (!value) return undefined

  try {
    return JSON.parse(value) as SearchDateSelection
  } catch {
    return undefined
  }
}

export function markTypeaheadDateAcknowledgementRequired(typeaheadId: string | undefined) {
  if (!typeaheadId) return
  safeStorageSetItem(dateAcknowledgementKey(typeaheadId), 'true')
}

function clearTypeaheadDateAcknowledgement(typeaheadId: string | undefined) {
  if (!typeaheadId) return
  safeStorageRemoveItem(dateAcknowledgementKey(typeaheadId))
}

/**
 * Persist confirmation state before navigating for an in-flight NL extract.
 * If the user already reviewed dates on the form, align the stored baseline with
 * the form dates and clear any pending results-page acknowledgement.
 * Otherwise keep the pre-extract baseline so the results page can soft-ack.
 */
export function persistTypeaheadDateConfirmationForNavigation({
  typeaheadId,
  hasReviewedDates,
  baselineSelection,
  formSelection,
}: {
  typeaheadId: string | undefined
  hasReviewedDates: boolean
  baselineSelection: SearchDateSelection | undefined
  formSelection: SearchDateSelection
}) {
  if (!typeaheadId) return

  if (hasReviewedDates) {
    storeTypeaheadDateConfirmationBaseline(typeaheadId, formSelection)
    clearTypeaheadDateAcknowledgement(typeaheadId)
    return
  }

  storeTypeaheadDateConfirmationBaseline(
    typeaheadId,
    baselineSelection ?? formSelection,
  )
}

export function consumeTypeaheadDateAcknowledgement(typeaheadId: string | undefined): boolean {
  if (!typeaheadId) return false

  const key = dateAcknowledgementKey(typeaheadId)
  const shouldAcknowledge = safeStorageGetItem(key) === 'true'
  safeStorageRemoveItem(key)
  return shouldAcknowledge
}
